import { Theme } from "@amx/ux-twoui-theming";
import { FontWeights, HighContrastSelector, ILinkStyles, IStackStyles } from "@fluentui/react";
import React from "react";
import { IconName } from "../../../styles/icons";
import { media } from "../../../styles/media";

export const getInfoColumnStyles = (): IStackStyles => {
    return {
        root: {
            selectors: {
                [media.maxS]: {
                    paddingTop: "4px",
                    paddingBottom: "8px"
                },
                [media.minM]: {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                },
                [media.minXl]: {
                    paddingTop: "29px",
                    paddingBottom: "19px"
                }
            }
        }
    };
};

export const getInfoColumnLinkStyle = (): React.CSSProperties => {
    return {
        textDecoration: "none",
        boxShadow: "none",
        display: "inline-block"
    };
};

export const getInfoColumnLinkStyles = (theme: Theme, iconName: IconName, isHyperlinkVariant?: boolean): ILinkStyles => {
    const iconClassName = `.${iconName}`;

    const ctaColor = isHyperlinkVariant ? theme.palette.themeDarkAlt : theme.palette.neutralPrimary;
    const ctaHoverColor = isHyperlinkVariant ? theme.palette.themePrimary : theme.palette.themeSecondary;

    const infoHoverColor = ctaHoverColor;
    const infoActiveColor = isHyperlinkVariant ? theme.palette.themeDarkAlt : theme.palette.themePrimary;

    return {
        root: {
            maxWidth: "100%",
            selectors: {
                ".FullCircleMask": {
                    color: theme.palette.themeLighter,
                },
                [iconClassName]: {
                    color: theme.palette.themePrimary
                },
                ".infoColumnCta, .ChevronRight, .ChevronLeft": {
                    color: ctaColor
                },
                ".ChevronRight, .ChevronLeft": {
                    display: "block",
                    paddingLeft: "13px",
                    paddingRight: "52px",
                    transform: "translateY(4px)",
                    [media.maxM]: {
                        transform: "translateY(8px)"
                    }
                },
                ".infoColumnInfoText": {
                    color: theme.palette.neutralSecondary
                },
                ":focus": {
                    outline: `1px solid ${theme.palette.neutralPrimary}`,
                    selectors: {
                        ".FullCircleMask": {
                            color: theme.palette.themeLighter
                        },
                        [iconClassName]: {
                            color: theme.palette.themePrimary
                        },
                        ".infoColumnCta, .ChevronRight, .ChevronLeft": {
                            color: ctaColor
                        },
                        ".infoColumnInfoText": {
                            color: theme.palette.neutralSecondary
                        }
                    }
                },
                ":hover": {
                    selectors: {
                        ".FullCircleMask": {
                            color: theme.palette.themeSecondary
                        },
                        [iconClassName]: {
                            color: theme.palette.neutralHigh
                        },
                        ".infoColumnCta, .ChevronRight, .ChevronLeft": {
                            color: ctaHoverColor
                        },
                        ".infoColumnInfoText": {
                            color: infoHoverColor
                        }
                    }
                },
                ":active:focus": {
                    outline: "0px",
                    selectors: {
                        ".FullCircleMask": {
                            color: theme.palette.themePrimary
                        },
                        [iconClassName]: {
                            color: theme.palette.neutralHigh
                        },
                        ".infoColumnCta, .ChevronRight, .ChevronLeft": {
                            color: theme.palette.themePrimary
                        },
                        ".infoColumnInfoText": {
                            color: infoActiveColor
                        },
                    }
                },
                [HighContrastSelector]: {
                    ".FullCircleMask, .infoColumnCta, .infoColumnInfoText": {
                        color: "LinkText"
                    },
                    [iconClassName]: {
                        color: "Window"
                    },
                    ":hover, &:focus": {
                        ".FullCircleMask, .infoColumnCta, .infoColumnInfoText, .ChevronRight, .ChevronLeft": {
                            color: "highlight"
                        },
                        [iconClassName]: {
                            color: "Window"
                        }
                    }
                }
            }
        }
    };
};

export const getIconGeneralStyles = (iconName?: IconName): IStackStyles => {
    const iconClassName = `.${iconName}`;

    return {
        root: {
            float: "left",
            width: "32px",
            marginLeft: "5px",
            marginRight: "13px",
            selectors: {
                ".FullCircleMask": {
                    float: "left" as "left",
                    fontSize: "32px",
                    lineHeight: "32px"
                },
                [iconClassName]: {
                    float: "left" as "left",
                    fontSize: "16px",
                    lineHeight: "32px",
                    marginLeft: "-24px"
                },
                [HighContrastSelector]: {
                    [iconClassName]: {
                        "-webkit-forced-color-adjust": "none",
                        "forced-color-adjust": "none"
                    }
                }
            }
        }
    };
};

export const getIconAlertBadgeStyles = (rtl?: boolean): IStackStyles => {
    return {
        root: {
            position: "relative",
            selectors: {
                ".StatusCircle7, .StatusWarning": {
                    // If RTL, mirror the badge to the left side of the icon
                    transform: rtl ? "rotateY(180deg)" : undefined
                },
                ".StatusCircle7": {
                    position: "absolute",
                    left: "5px",
                    top: "-3px"
                },
                ".StatusWarning": {
                    position: "absolute",
                    left: "12.75px",
                    top: "5px"
                },
                [HighContrastSelector]: {
                    ".StatusWarning, .StatusCircle7": {
                        "-webkit-forced-color-adjust": "none",
                        "forced-color-adjust": "none"
                    }
                }
            }
        }
    };
};

export const getInfoGeneralStyles = (isHyperlinkVariant?: boolean): IStackStyles => {

    return {
        root: {
            maxWidth: "100%",
            marginRight: isHyperlinkVariant ? undefined : "13px",
            selectors: {
                ".infoColumnCta": {
                    fontWeight: FontWeights.semibold,
                    selectors: {
                        [media.maxM]: {
                            lineHeight: "16px",
                            marginTop: "8px"
                        },
                        [media.minL]: {
                            lineHeight: "16px"
                        }
                    }
                },
                ".infoColumnInfoText": {
                    lineHeight: "16px",
                    selectors: {
                        [media.maxS]: {
                            display: "none" as "none"
                        },
                        [media.minM]: {
                            display: "none" as "none"
                        },
                        [media.minL]: {
                            display: "block" as "none"
                        }
                    }
                }
            }
        }
    };
};
